import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./styles/CyberSecurityInfrastructure.css";

const resilienceTabs = [
  {
    key: "segmentation",
    label: "Segmentation",
    title: "Set stronger security boundaries across the network",
    text: "ComNetworks helps measure and improve digital resilience through network segmentation and network protection implementation, creating clearer zones and stronger control over how systems connect.",
    stats: [
      "Network segmentation",
      "Network protection implementation",
      "Reduced lateral exposure across environments",
    ],
  },
  {
    key: "intrusion",
    label: "Detection & Prevention",
    title: "Detect, block, and respond to suspicious activity",
    text: "Wireless intrusion protection systems plus intrusion detection and prevention services help improve visibility into threats and strengthen active defense across wired and wireless infrastructure.",
    stats: [
      "Wireless intrusion protection system",
      "Intrusion detection and prevention",
      "Higher visibility into network events",
    ],
  },
  {
    key: "compliance",
    label: "Compliance",
    title: "Support compliance alignment with clearer controls and auditability",
    text: "HIPAA and PCI related efforts depend on disciplined control boundaries, security measures, audit readiness, and infrastructure practices that protect sensitive systems and data.",
    stats: [
      "HIPAA and PCI compliance support",
      "WLAN auditing",
      "Evidence friendly infrastructure review",
    ],
  },
  {
    key: "testing",
    label: "Testing & Privacy",
    title: "Validate resilience through testing and data protection measures",
    text: "Vulnerability assessments, penetration testing, and data security and privacy management help identify weaknesses, improve resilience, and support better protection of sensitive information.",
    stats: [
      "Vulnerability and penetration testing",
      "Data security and privacy management",
      "Risk visibility for remediation planning",
    ],
  },
];

const securityZones = [
  {
    title: "Control zones",
    text: "Use segmentation to separate systems, users, and sensitive areas into more controlled security boundaries.",
  },
  {
    title: "Wireless protection zones",
    text: "Strengthen wireless environments with intrusion protection, auditing, and better policy enforcement visibility.",
  },
  {
    title: "Compliance sensitive zones",
    text: "Support environments tied to HIPAA, PCI, and similar obligations with clearer isolation and control measures.",
  },
  {
    title: "Testing and review zones",
    text: "Use vulnerability review and penetration testing to understand exposure across infrastructure and trust boundaries.",
  },
];

const matrixItems = [
  {
    title: "WLAN auditing",
    text: "Review wireless environments for configuration, exposure, policy alignment, and operational security posture.",
  },
  {
    title: "Vulnerability testing",
    text: "Identify weaknesses that could affect resilience, access control, or infrastructure stability.",
  },
  {
    title: "Penetration testing",
    text: "Validate how real world attack paths might move through the environment and where controls need strengthening.",
  },
  {
    title: "Privacy management",
    text: "Align infrastructure practices with the handling and protection of sensitive or regulated information.",
  },
];

const fitCards = [
  {
    title: "Compliance driven organizations",
    text: "For businesses that need infrastructure controls aligned with HIPAA, PCI, or similar security obligations.",
  },
  {
    title: "Networks needing stronger boundaries",
    text: "For environments where segmentation, intrusion protection, and monitored trust zones are becoming more important.",
  },
  {
    title: "Wireless environments under review",
    text: "For teams that need WLAN auditing and stronger wireless protection across access points and connected devices.",
  },
  {
    title: "Organizations improving cyber resilience",
    text: "For businesses that want ongoing measurement, benchmarking, testing, and privacy aware infrastructure management.",
  },
];

export default function CyberSecurityInfrastructure() {
  const [activeKey, setActiveKey] = useState("segmentation");
  const activeTab =
    resilienceTabs.find((tab) => tab.key === activeKey) || resilienceTabs[0];

  return (
    <div className="cyberInfraPage">
      <section className="cyberInfraHero">
        <div className="cyberInfraContainer cyberInfraHero__container">
          <div className="cyberInfraHero__content">
            <p className="cyberInfraEyebrow">Cyber Security Infrastructure</p>

            <h1 className="cyberInfraHero__title">
              Measure resilience, strengthen boundaries, and actively manage security posture.
            </h1>

            <p className="cyberInfraHero__text">
              ComNetworks helps benchmark and improve the digital resilience of
              your network and security infrastructure through segmentation,
              intrusion protection, compliance support, WLAN auditing,
              penetration testing, and data security management.
            </p>

            <div className="cyberInfraHero__actions">
              <Link to="/contact" className="cyberInfraBtn cyberInfraBtn--primary">
                Talk to Our Team
              </Link>
              <Link to="/services" className="cyberInfraBtn cyberInfraBtn--secondary">
                Explore Services
              </Link>
            </div>
          </div>

          <div className="cyberInfraHero__visual">
            <div className="resilienceShell">
              <div className="resilienceShell__top">
                <span className="resilienceTag">Digital resilience</span>
                <span className="resilienceTag resilienceTag--soft">Active controls</span>
              </div>

              <div className="resilienceBoard">
                <div className="resilienceScore">
                  <span className="resilienceScore__label">Posture</span>
                  <strong>Secure</strong>
                </div>

                <div className="resilienceLayer resilienceLayer--one">
                  <span>Segmentation</span>
                </div>
                <div className="resilienceLayer resilienceLayer--two">
                  <span>Detection</span>
                </div>
                <div className="resilienceLayer resilienceLayer--three">
                  <span>Compliance</span>
                </div>
                <div className="resilienceLayer resilienceLayer--four">
                  <span>Testing</span>
                </div>
              </div>

              <div className="resilienceShell__bottom">
                <div className="resilienceFootCard">
                  <strong>Benchmark and target</strong>
                  <span>Track resilience through measurable security layers</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cyberModesSection">
        <div className="cyberInfraContainer">
          <div className="cyberInfraSectionHead cyberInfraSectionHead--center">
            <p className="cyberInfraEyebrow">Security modes</p>
            <h2 className="cyberInfraTitle">
              A new tab style: mode based security panels with embedded status info
            </h2>
            <p className="cyberInfraText">
              This page avoids the earlier tab styles and instead uses a compact
              security mode switcher with embedded evidence style detail blocks.
            </p>
          </div>

          <div className="cyberModesWrap">
            <div className="cyberModesNav">
              {resilienceTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`cyberModeBtn ${activeKey === tab.key ? "active" : ""}`}
                  onClick={() => setActiveKey(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="cyberModesPanel">
              <div className="cyberModesPanel__intro">
                <p className="cyberModesLabel">{activeTab.label}</p>
                <h3>{activeTab.title}</h3>
                <p>{activeTab.text}</p>
              </div>

              <div className="cyberModesPanel__stats">
                {activeTab.stats.map((item) => (
                  <div key={item} className="cyberStatRow">
                    <span className="cyberStatRow__signal" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cyberZonesSection">
        <div className="cyberInfraContainer">
          <div className="cyberInfraSectionHead">
            <p className="cyberInfraEyebrow">Security architecture</p>
            <h2 className="cyberInfraTitle">
              Unique section style: security zones arranged like a control architecture
            </h2>
            <p className="cyberInfraText">
              This layout uses zone style boxes rather than ordinary feature cards
              so the page feels more like a security infrastructure interface.
            </p>
          </div>

          <div className="cyberZonesGrid">
            {securityZones.map((item) => (
              <article key={item.title} className="cyberZoneCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="cyberComplianceSection">
        <div className="cyberInfraContainer">
          <div className="cyberComplianceBand">
            <div className="cyberComplianceBand__left">
              <p className="cyberInfraEyebrow">Compliance and controls</p>
              <h2 className="cyberInfraTitle">
                HIPAA, PCI, and infrastructure control practices work best when the environment is clearly segmented and reviewable
              </h2>
            </div>

            <div className="cyberComplianceBand__right">
              <p className="cyberInfraText">
                Compliance support is stronger when network boundaries,
                wireless auditing, intrusion protections, and data security
                practices are managed together rather than separately.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="cyberMatrixSection">
        <div className="cyberInfraContainer">
          <div className="cyberInfraSectionHead cyberInfraSectionHead--center">
            <p className="cyberInfraEyebrow">Validation matrix</p>
            <h2 className="cyberInfraTitle">
              Testing, auditing, and privacy controls shown in a matrix layout
            </h2>
            <p className="cyberInfraText">
              This matrix style section is intentionally different from earlier
              rows and grids, giving the page a more security review feel.
            </p>
          </div>

          <div className="cyberMatrixGrid">
            {matrixItems.map((item, index) => (
              <article
                key={item.title}
                className={`cyberMatrixCard ${index % 2 !== 0 ? "cyberMatrixCard--alt" : ""}`}
              >
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="cyberPrivacySection">
        <div className="cyberInfraContainer">
          <div className="cyberPrivacyPanel">
            <div className="cyberPrivacyPanel__content">
              <p className="cyberInfraEyebrow">Privacy and resilience</p>
              <h2 className="cyberInfraTitle">
                Data security, privacy management, and active defense belong in the same infrastructure strategy
              </h2>
              <p className="cyberInfraText">
                A resilient security posture is not only about blocking threats.
                It also involves protecting sensitive information, validating
                controls, and improving how the infrastructure behaves under pressure.
              </p>
            </div>

            <div className="cyberPrivacyPanel__stack">
              <div className="cyberStackCard">
                <strong>Auditability</strong>
                <span>WLAN review, compliance alignment, and clearer security evidence</span>
              </div>
              <div className="cyberStackCard">
                <strong>Protection</strong>
                <span>Intrusion prevention, segmentation, and active infrastructure controls</span>
              </div>
              <div className="cyberStackCard">
                <strong>Validation</strong>
                <span>Vulnerability and penetration testing for resilience improvement</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cyberFitSection">
        <div className="cyberInfraContainer">
          <div className="cyberInfraSectionHead cyberInfraSectionHead--center">
            <p className="cyberInfraEyebrow">Best fit</p>
            <h2 className="cyberInfraTitle">
              Where Cyber Security Infrastructure services create the most value
            </h2>
            <p className="cyberInfraText">
              These services are especially useful in environments where security
              boundaries, compliance readiness, wireless review, and resilience testing matter.
            </p>
          </div>

          <div className="cyberFitGrid">
            {fitCards.map((item) => (
              <article key={item.title} className="cyberFitCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="cyberCta">
        <div className="cyberInfraContainer cyberCta__container">
          <div className="cyberCta__content">
            <p className="cyberInfraEyebrow cyberInfraEyebrow--light">Next step</p>
            <h2 className="cyberCta__title">
              Improve cyber resilience with stronger controls, clearer boundaries, and better validation
            </h2>
            <p className="cyberCta__text">
              If you need help with segmentation, intrusion protection,
              compliance related infrastructure practices, WLAN auditing,
              vulnerability testing, or privacy management, ComNetworks can help
              define the right security infrastructure approach.
            </p>
          </div>

          <div className="cyberCta__actions">
            <Link to="/contact" className="cyberInfraBtn cyberInfraBtn--primary">
              Contact Us
            </Link>
            <Link to="/services" className="cyberInfraBtn cyberInfraBtn--ghost">
              View More Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
